<template>
  <div class="scenario">
    <div class="scenario__main">
      <div
        class="scenario__chessboard"
        :class="{ 'hide-moves': !getShowMoveTable }"
        :style="boardStyle"
      >
        <Chessboard
          ref="chessboard"
          :orientation="boardOrientation"
          :pgn="currentMove.pgn"
          :initialFen="pgnData.fen"
          :movable="nextColor"
          @onAfterMove="handleBoardMove"
        />
        <BoardControls
          class="board-controls"
          :show-board-size="!isPresentationMode"
          :show-flip="true"
          :show-fen-copy="!!puzzleStatus"
          :show-pgn-copy="!!puzzleStatus"
          :show-free-mode="!!puzzleStatus"
          :is-presentation-mode="isPresentationMode"
          :fen-value="currentMove.fen || pgnData.fen || ''"
          :pgn-value="
            pgnData.toString({
              includeUserScore: false,
              stopAtMoveId: currentMove.id,
            })
          "
          :disabled-free-mode="!puzzleStatus"
          @size-changed="handleBoardSizeChange"
          @flip="flipBoard"
          @free-mode="goToFreeMode"
        />
      </div>

      <div
        v-show="getShowMoveTable && !isPresentationMode"
        class="scenario__moves"
        :style="movesTableStyle"
      >
        <ScenarioMovesTable
          :pgnData="pgnData"
          :currentMove="currentMove"
          :isPresentationMode="isPresentationMode"
          @moveSelected="goToMove"
        />
        <div class="scenario__status">
          <div
            v-if="puzzleStatus === 'correct'"
            class="has-text-success"
            role="status"
          >
            <span>Puzzle Complete! </span> <span aria-hidden="true">🎉</span>
          </div>
          <div
            v-else-if="puzzleStatus === 'incorrect'"
            class="has-text-danger"
            role="alert"
          >
            <span>Puzzle Failed! </span> <span aria-hidden="true">❌</span>
          </div>
          <div
            v-if="puzzleStatus === 'incorrect' || puzzleStatus === 'correct'"
            class="has-text-danger attempt-failed-actions"
            role="alert"
          >
            <b-button
              v-if="!quizMode && solutionStatus !== 0"
              type="is-primary"
              size="is-small"
              icon-left="redo"
              @click="retryPuzzle"
            >
              Retry
            </b-button>
            <b-button
              v-if="!quizMode && solutionStatus === -1"
              type="is-info"
              size="is-small"
              icon-left="eye"
              @click="revealSolution"
            >
              Solution
            </b-button>
          </div>
          <div
            v-else-if="wrongMoveCount > 0 && currentMoveScore === -1"
            class="has-text-danger"
            role="alert"
          >
            <p>Incorrect move. Try again!</p>
          </div>
          <div v-if="isWaitingForUserInput">
            <p>Make the best move for {{ nextColor }}.</p>
            <div v-if="maxFailedAttempts > 1">
              <p>
                {{ remainingAttempts }}
                {{ remainingAttempts > 1 ? "attempts" : "attempt" }} remaining.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { PgnData } from "vue-chess-shared/pgn/PgnData";
import { PgnDataBuilder } from "vue-chess-shared/pgn/PgnDataBuilder";
import Chess from "chess.js";
import Chessboard from "@/components/games/Chessboard";
import confetti from "canvas-confetti";
import ScenarioMovesTable from "@/components/training/moves/ScenarioMovesTable";
import BoardControls from "./BoardControls.vue";
import { boardSizeMixin } from "@/mixins/boardSizeMixin";
import scenarioSyncMixin from "@/mixins/scenarioSyncMixin";

export default {
  name: "ScenarioPuzzle",

  mixins: [boardSizeMixin, scenarioSyncMixin],

  components: {
    BoardControls,
    Chessboard,
    ScenarioMovesTable,
  },

  props: {
    pgn: {
      type: String,
      required: true,
    },

    answerPgn: {
      type: String,
      required: false,
    },

    answerStatus: {
      type: String,
      required: false,
    },

    initialFen: {
      type: String,
      default: "start",
    },

    maxFailedAttempts: {
      type: Number,
      default: 2,
      validator: (value) => value > 0,
    },

    quizMode: {
      type: Boolean,
      default: false,
    },

    isPresentationMode: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      chess: new Chess(),
      puzzleStatus: null,
    };
  },

  computed: {
    solutionStatus() {
      return this.getScenarioState?.solutionStatus;
    },

    wrongMoveCount() {
      return this.pgnData?.userMoves.filter((move) => move.userScore === -1)
        .length;
    },

    remainingAttempts() {
      return Math.max(0, this.maxFailedAttempts - this.wrongMoveCount);
    },

    nextColor() {
      if (this.currentMove && this.currentMove.color) {
        return this.currentMove.color === "b" ? "white" : "black";
      }
      if (this.pgnData && this.pgnData.fen) {
        return this.pgnData.fen.includes(" b ") ? "black" : "white";
      }
      return "white";
    },

    // Determine if the last move was correct based on the current move's user score
    currentMoveScore() {
      return this.currentMove?.userScore;
    },

    // Determine if the component is waiting for user input
    // to decide if we should show the 'Play a move' label
    isWaitingForUserInput() {
      if (this.puzzleStatus) {
        return false;
      }

      // Find the next move in the sequence that the user is expected to play
      const nextExpectedMove = this.currentMove?.id
        ? this.currentMove.nextMove
        : this.pgnData?.rootMove;

      // Show prompt only if the next expected move exists and is a "question" move
      return !!nextExpectedMove?.isQuestion;
    },
  },

  watch: {
    pgn: {
      immediate: true,
      handler(newPgn) {
        if (!newPgn || typeof newPgn !== "string") return;
        if (this.answerPgn) return;
        this.loadScenario(newPgn);
      },
    },

    answerPgn: {
      immediate: true,
      handler(newPgn) {
        if (!newPgn || typeof newPgn !== "string") return;
        this.loadScenario(newPgn);
        this.puzzleStatus = this.answerStatus;
      },
    },

    // Watch for changes in puzzleStatus to sync with store and show toasts
    puzzleStatus: {
      handler(newStatus) {
        console.log(
          "puzzleStatus changed:",
          newStatus,
          this.getScenarioState?.puzzleStatus
        );

        // Sync puzzleStatus with store
        if (newStatus !== this.getScenarioState?.puzzleStatus) {
          this.$store.commit("setScenarioState", {
            puzzleStatus: newStatus,
          });
        }

        // Show toast based on puzzle status
        if (newStatus === "correct") {
          this.$buefy.toast.open({
            message: "🎉 Puzzle completed successfully!",
            type: "is-success",
            position: "is-top",
            duration: 3000,
          });
        } else if (newStatus === "incorrect") {
          this.$buefy.toast.open({
            message: "✗ Puzzle failed!",
            type: "is-danger",
            position: "is-top",
            duration: 3000,
          });
        }
      },
    },

    // Watch for changes in puzzleStatus from other windows
    "getScenarioState.puzzleStatus": {
      handler(newStatus) {
        console.log(
          "getScenarioState.puzzleStatus changed:",
          newStatus,
          this.puzzleStatus
        );

        // Skip if this is the window that initiated the change
        if (newStatus === this.puzzleStatus) {
          return;
        }
        this.puzzleStatus = newStatus;
      },
    },

    // Watch for changes in currentMoveScore to show toasts for individual moves
    currentMoveScore: {
      handler(newScore, oldScore) {
        // Only show toast if score has changed and puzzleStatus is null
        if (newScore !== oldScore && this.puzzleStatus === null) {
          if (newScore === 1) {
            this.$buefy.toast.open({
              message: "✓ Correct move!",
              type: "is-success",
              position: "is-top",
              duration: 2000,
            });
          } else if (newScore === -1) {
            this.$buefy.toast.open({
              message: "✗ Incorrect move!",
              type: "is-danger",
              position: "is-top",
              duration: 2000,
            });
          }
        }
      },
    },

    solutionStatus: {
      handler(newStatus) {
        if (newStatus === 0) {
          this.$buefy.toast.open({
            message: "Playing solution...",
            type: "is-info",
            position: "is-top",
            duration: 2000,
          });
        }
      },
    },
  },

  methods: {
    resetState() {
      this.chess.reset();
      this.currentMove = {};
      this.pgnData = PgnData.createEmpty(new Chess());
      this.puzzleStatus = null;
      this.$nextTick(() => {
        this.$store.commit("setScenarioState", {
          solutionStatus: -1,
        });
      });
    },

    loadScenario(pgn) {
      if (!pgn || typeof pgn !== "string") return;

      this.resetState();

      try {
        const pgnDataBuilder = new PgnDataBuilder(pgn, new Chess());
        this.pgnData = pgnDataBuilder.pgnData();
        if (this.pgnData.userMoves.length > 0) {
          this.goToMove(
            this.pgnData.userMoves[this.pgnData.userMoves.length - 1]
          );
        } else {
          this.goToMove({});
        }
        this.determineAndSetPuzzleOrientation();
      } catch (error) {
        console.error("Error loadScenario:", error);
        this.$buefy.toast.open({
          message: "Failed to load example: Invalid PGN format",
          type: "is-danger",
        });
      }
    },

    determineAndSetPuzzleOrientation() {
      if (!this.pgnData || !this.pgnData.isPuzzle) {
        return;
      }

      // Find the first question move in the puzzle
      const firstQuestionMove = this.findFirstQuestionMove(
        this.pgnData.rootMove
      );
      if (!firstQuestionMove) return;

      const sideToMove = firstQuestionMove.color === "w" ? "white" : "black";

      if (this.boardOrientation !== sideToMove) {
        this.$store.dispatch("updateCoachingOrientation", sideToMove);
        this.syncState();
      }
    },

    findFirstQuestionMove(rootMove) {
      if (!rootMove) return null;

      let currentMove = rootMove;
      while (currentMove) {
        if (currentMove.isQuestion) {
          return currentMove;
        }
        currentMove = currentMove.nextMove;
      }
      return null;
    },

    goToMove(move) {
      if (!move || !move.id) {
        this.currentMove = {};
        return;
      }

      // Only allow going to revealed moves
      if (!move.isPuzzleMove || !move.mask) {
        this.currentMove = move;
      }
    },

    handleBoardMove(moveData) {
      // Get the next expected move in the puzzle sequence
      const nextExpectedMove = this.currentMove?.id
        ? this.currentMove.nextMove
        : this.pgnData.rootMove;

      let userMove = null;
      if (nextExpectedMove) {
        // Check if the move matches the expected puzzle move
        userMove = nextExpectedMove.findMatchedPuzzleMove(moveData.move);
      }

      // no matched move, add new move
      if (!userMove) {
        userMove = this.pgnData.addMove(moveData.move, this.currentMove.id);
      }

      if (!userMove) {
        console.error("Failed to add new move", moveData.move);
        return;
      }

      userMove.recordUserMove();
      this.handleUserMove(userMove);
    },

    handleUserMove(answerMove) {
      this.$refs.chessboard.engine.undo();

      // Go to the move first so currentMoveScore computed property will be updated
      this.goToMove(answerMove);

      if (answerMove.userScore === 1) {
        if (answerMove.nextMove) {
          this.revealNextMove(answerMove);
        } else {
          this.completePuzzle();
        }
      } else if (answerMove.userScore === -1) {
        this.handleIncorrectMove(answerMove);
      }
    },

    handleIncorrectMove(move) {
      this.goToMove(move);

      if (this.wrongMoveCount >= this.maxFailedAttempts) {
        this.failPuzzle();
      } else {
        this.resetToPreviousPosition();
      }
    },

    revealNextMove(matchedMove) {
      setTimeout(() => {
        matchedMove.nextMove.recordRevealed();
        this.goToMove(matchedMove.nextMove);
      }, 1000);
    },

    resetToPreviousPosition() {
      setTimeout(() => {
        this.goToMove(this.currentMove.previousMove);
      }, 500);
    },

    completePuzzle() {
      this.puzzleStatus = "correct";
      this.$emit("puzzle-complete", this.pgnText);

      if (!this.quizMode) {
        setTimeout(async () => {
          try {
            await this.revealSolution(false);
          } catch (error) {
            console.error("Error during automatic solution reveal:", error);
          }
        }, 1000);
      }
      this.firePuzzleCompletionConfetti();
    },

    failPuzzle() {
      this.puzzleStatus = "incorrect";
      this.$refs.chessboard.engine.undo();
      this.$emit("puzzle-failed", this.pgnText);
    },

    async revealSolution() {
      const firstPuzzleMove = this.goToPuzzlePosition();
      await this.$nextTick();

      this.$store.commit("setScenarioState", {
        solutionStatus: 0,
      });

      await this.revealAllMoves(firstPuzzleMove, 800);

      this.$store.commit("setScenarioState", {
        solutionStatus: 1,
      });
    },

    revealMove(move) {
      if (!move?.id) return;
      if (move.mask) {
        move.recordRevealed();
      }
      this.goToMove(move);
    },

    goToPuzzlePosition() {
      if (!this.pgnData.rootMove) return;

      const firstQuestionMove = this.findFirstQuestionMove(
        this.pgnData.rootMove
      );

      if (firstQuestionMove) {
        const previousMove = firstQuestionMove.previousMove;
        if (previousMove) {
          this.currentMove = previousMove;
        } else {
          this.currentMove = {};
        }
        return firstQuestionMove;
      }
    },

    // Recursively reveal all moves in the puzzle with sequential timing
    async revealAllMoves(move, delay = 800) {
      if (!move?.id) {
        const firstPuzzleMove = this.goToPuzzlePosition();
        if (firstPuzzleMove) {
          await this.revealAllMoves(firstPuzzleMove, delay);
        }
        return;
      }

      // Reveal current move
      if (delay > 0) {
        await new Promise((resolve) => setTimeout(resolve, delay));
        this.revealMove(move);
      } else {
        move.recordRevealed();
      }

      // Process next move in main line
      if (move.nextMove) {
        await this.revealAllMoves(move.nextMove, delay);
      }

      // Process variations
      if (move.childMoves?.length) {
        // Process variations sequentially
        for (const childMove of move.childMoves) {
          await this.revealAllMoves(childMove, 0);
        }
      }
    },

    retryPuzzle() {
      this.loadScenario(this.pgn);
      this.goToPuzzlePosition();
      // Show retry toast
      this.$buefy.toast.open({
        message: "Puzzle reset. Try again!",
        type: "is-info",
        position: "is-top",
        duration: 2000,
      });
    },

    firePuzzleCompletionConfetti() {
      const duration = 2000;
      const end = Date.now() + duration;

      const colors = ["#ffd700", "#00ff00", "#0099ff"];

      (function frame() {
        confetti({
          particleCount: 3,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: colors,
        });
        confetti({
          particleCount: 3,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: colors,
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      })();

      // Fire a final burst
      setTimeout(() => {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: colors,
        });
      }, duration - 500);
    },

    goToFreeMode() {
      const currentFen = this.currentMove?.fen || this.pgnData.fen;
      this.$emit("enterFreeMode", currentFen);
    },
  },
};
</script>

<style scoped>
/* Keep only styles that are specific to ScenarioPuzzle and not in scenario-board.css */
.attempt-failed-actions {
  /* height: 100%; */
  display: flex;
  gap: 1rem;
  justify-content: center;
  align-items: center;
}

.button {
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
}

.button:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

@import "../../../assets/styles/scenario-board.css";
</style>
